import ClearIcon from "@mui/icons-material/Clear";
import Upload from "@mui/icons-material/Upload";
import { Container, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider";
import * as Messages from "../messages/messages";
import { useDeleteFile, useUploadFiles } from "../Slides/Hooks/fileQueries";
import "./styles/file-field.css";

const CustomUploadProgress = () => {
  return (
    <Box id="uploadProgressContainer">
      <Typography id="uploadProgressText" variant="h6">
        Uploading Files
      </Typography>
      <Box
        id="uploadProgress"
        sx={{ position: "relative", display: "inline-flex" }}
      >
        <CircularProgress
          id="uploadFileProgress"
          classes={{
            svg: "uploadFileProgressSvg",
          }}
        />
      </Box>
    </Box>
  );
};

const UploadFileField = ({
  idProp,
  state,
  setState,
  disabledInput,
  isUploading,
  setIsUploading,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadProgress, setUploadProgress] = useState({ [idProp]: false });

  const {
    setIsLoading,
    handleInputDisable,
    disableAll,
    requestStatus,
    setOpenBackdrop,
  } = useContext(FormContext);

  const { userId, clientId, projectId } = useContext(DashboardContext);

  const deleteFile = useDeleteFile(
    clientId,
    projectId,
    idProp,
    state,
    setState,
    enqueueSnackbar,
  );
  const uploadFormFiles = useUploadFiles(setIsUploading, setState);

  useEffect(() => {
    console.log(state[idProp]);
  }, [state]);

  const uploadFileHandler = async (files) => {
    try {
      const uploadedFiles = await uploadFormFiles.mutateAsync({
        clientId,
        projectId,
        idProp,
        files,
        userId,
      });

      const allUploadedFiles = uploadedFiles
        .map((file) => file.data.files)
        .flat();

      setState((prevState) => ({
        ...prevState,
        [idProp]: {
          ...prevState[idProp],
          files: [...prevState[idProp].files, ...allUploadedFiles],
        },
      }));

      enqueueSnackbar(`All files uploaded successfully.`, {
        variant: "success",
        autoHideDuration: 2500,
      });
    } catch (err) {
      enqueueSnackbar(`Error uploading files. ${err.message}`, {
        variant: "error",
        autoHideDuration: 2500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (requestStatus === "P" || requestStatus === "RV") return;

    const uploadedFiles = Array.from(e.dataTransfer.files);

    if (uploadedFiles.length === 0) return;

    if (uploadedFiles.length > 5) {
      enqueueSnackbar("You can only upload up to 5 files at a time.", {
        variant: "warning",
        autoHideDuration: 2500,
      });
    }
    setOpenBackdrop(true);
    setIsUploading(true);
    setUploadProgress({ [idProp]: true });
    setIsLoading(true);

    await uploadFileHandler(uploadedFiles);

    setIsUploading(false);
    setUploadProgress({ [idProp]: false });
    setIsLoading(false);
    setOpenBackdrop(false);
  };

  const handleFileSelect = async (e) => {
    const uploadedFiles = Array.from(e.target.files);

    if (requestStatus === "P" || requestStatus === "RV") return;

    if (uploadedFiles.length === 0) return;

    if (uploadedFiles.length > 5) {
      enqueueSnackbar("You can only upload up to 5 files at a time.", {
        variant: "warning",
        autoHideDuration: 2500,
      });
    }
    setOpenBackdrop(true);
    setIsUploading(true);
    setUploadProgress({ [idProp]: true });
    setIsLoading(true);

    await uploadFileHandler(uploadedFiles);

    setOpenBackdrop(false);
    setIsUploading(false);
    setUploadProgress({ [idProp]: false });
    setIsLoading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Container id="uploadFilesContainer">
      <Box
        id="dragDropContainer"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={() => {
          document.getElementById("hidden-input").click();
        }}
        disabled={
          isUploading ||
          disableAll ||
          handleInputDisable(idProp, state, disabledInput, userId)
        }
        style={{
          cursor:
            isUploading ||
            disableAll ||
            handleInputDisable(idProp, state, disabledInput, userId)
              ? "not-allowed"
              : "pointer",
          pointerEvents:
            isUploading ||
            disableAll ||
            handleInputDisable(idProp, state, disabledInput, userId)
              ? "none"
              : "auto",
        }}
      >
        {(!isUploading || !uploadProgress[idProp]) && (
          <>
            <Upload id="uploadFileIcon" className="uploadFileIcon" />
            <Typography id="dragDropText" variant="h6">
              Click to choose a file, or drag and drop them here
            </Typography>
          </>
        )}
        {isUploading && uploadProgress[idProp] && <CustomUploadProgress />}
        <input
          id="hidden-input"
          type="file"
          onChange={handleFileSelect}
          multiple
          hidden
          disabled={
            isUploading ||
            disableAll ||
            handleInputDisable(idProp, state, disabledInput, userId)
          }
        />
      </Box>
      {state[idProp].files.length > 0 && (
        <Box id="uploadedFiles">
          <Box id="uploadedFilesList">
            {state[idProp].files.map((file, index) => (
              <Box className="file-status-box" key={file.id}>
                <Typography
                  className="uploaded-file-name"
                  onClick={(e) => {
                    // Open file.file in new tab
                    window.open(file.file, "_blank");
                  }}
                >
                  {file.fileName.length <= 28
                    ? file.fileName
                    : `${file.fileName.slice(0, 25)}...`}
                </Typography>
                <SvgIcon
                  component={ClearIcon}
                  className="delete-file-icon"
                  viewBox="3 3 18 18"
                  onClick={async (e) => {
                    await deleteFile.mutateAsync({
                      uploadId: file.id,
                    });
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

// FileField component is used to render a file input field in the form
const FileField = ({
  idProp,
  label,
  state,
  setState,
  disabledInput,
  isUploading,
  setIsUploading,
  invalidData = null,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="attach-file-container" id={`${idProp}-file-container`}>
      <div className="question-section-container">
        <label className="question-section">{label}</label>
        <Tooltip
          id="tf-tooltip"
          classes={{
            tooltip: "text-field-tooltip",
            popper: "text-field-popper",
            arrow: "text-field-arrow",
            tooltipPlacementLeft: "text-field-right",
          }}
          title={Messages[`${idProp}Message`] || ""}
          arrow
          placement={"right"}
        >
          <InfoCircle
            className="text-info-circ"
            color="white"
            size={30}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          />
        </Tooltip>
      </div>
      <div className="info-attach-file">
        <UploadFileField
          idProp={idProp}
          state={state}
          setState={setState}
          disabledInput={disabledInput}
          isUploading={isUploading}
          setIsUploading={setIsUploading}
        />
      </div>
    </div>
  );
};

export default FileField;
